import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Save, Power, Trash2, ShieldAlert, Pencil } from "lucide-react";

const emptyForm = { name: "", area: "", lat: "", lng: "", radius_km: "3", admin_email: "" };

export default function BranchesTab() {
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [emailEdits, setEmailEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [pin, setPin] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", area: "", lat: "", lng: "", radius_km: "" });
  const [staff, setStaff] = useState([]);
  const [staffEmail, setStaffEmail] = useState("");

  const load = async () => { try { const r = await api.get("/admin/branches"); setBranches(r.data); } catch { toast.error("Gagal memuat cabang"); } };
  useEffect(() => { load(); loadStaff(); }, []);

  const createBranch = async () => {
    if (!form.name.trim()) { toast.error("Nama cabang wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/admin/branches", {
        name: form.name, area: form.area,
        lat: parseFloat(form.lat) || 0, lng: parseFloat(form.lng) || 0,
        radius_km: parseFloat(form.radius_km) || 3, admin_email: form.admin_email,
      });
      await load();
      toast.success("Cabang baru ditambahkan (stok mulai kosong)");
      setForm(emptyForm);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah cabang"); }
    finally { setSaving(false); }
  };

  const branchEmails = (b) => (b.admin_emails?.length ? b.admin_emails : b.admin_email ? [b.admin_email] : []);

  const saveEmails = async (b, emails) => {
    try {
      await api.put(`/admin/branches/${b.id}/admin-email`, { emails });
      await load();
      toast.success(`Email ${b.name} diperbarui (${emails.length}/5)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan email"); }
  };

  const addEmail = async (b) => {
    const em = (emailEdits[b.id] || "").trim().toLowerCase();
    if (!em) { toast.error("Isi email dulu"); return; }
    const cur = branchEmails(b);
    if (cur.includes(em)) { toast.error("Email sudah terdaftar di cabang ini"); return; }
    if (cur.length >= 5) { toast.error("Maksimal 5 email per cabang"); return; }
    await saveEmails(b, [...cur, em]);
    setEmailEdits({ ...emailEdits, [b.id]: "" });
  };

  const removeEmail = async (b, em) => {
    await saveEmails(b, branchEmails(b).filter((x) => x !== em));
    toast.success(`${em} dilepas — akun itu kembali jadi pelanggan`);
  };

  const toggleActive = async (b) => {
    try {
      await api.patch(`/admin/branches/${b.id}`, { is_active: !b.is_active });
      await load();
      toast.success(b.is_active ? `${b.name} dinonaktifkan (tidak tampil di aplikasi)` : `${b.name} diaktifkan kembali`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };

  const loadStaff = async () => { try { const r = await api.get("/admin/staff-input"); setStaff(r.data); } catch {} };

  const addStaff = async () => {
    if (!staffEmail.trim()) { toast.error("Isi email staff"); return; }
    try {
      await api.post("/admin/staff-input", { email: staffEmail });
      toast.success(`${staffEmail.trim().toLowerCase()} aktif sebagai staff input — minta login Google untuk mulai`);
      setStaffEmail("");
      loadStaff();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah staff"); }
  };

  const deactivateStaff = async (email) => {
    try {
      await api.post("/admin/staff-input/deactivate", { email });
      toast.success(`${email} dinonaktifkan — kembali jadi konsumen biasa`);
      loadStaff();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menonaktifkan"); }
  };

  const startEdit = (b) => {
    setEditing(editing === b.id ? null : b.id);
    setEditForm({ name: b.name, area: b.area || "", lat: String(b.lat ?? ""), lng: String(b.lng ?? ""), radius_km: String(b.radius_km ?? 3) });
  };

  const saveEdit = async (b) => {
    if (!editForm.name.trim()) { toast.error("Nama cabang wajib diisi"); return; }
    try {
      await api.patch(`/admin/branches/${b.id}`, {
        name: editForm.name, area: editForm.area,
        lat: parseFloat(editForm.lat) || 0, lng: parseFloat(editForm.lng) || 0,
        radius_km: parseFloat(editForm.radius_km) || 3,
      });
      await load();
      setEditing(null);
      toast.success(`Data ${editForm.name} diperbarui`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan perubahan"); }
  };

  const deleteBranch = async (b) => {
    try {
      await api.post(`/admin/branches/${b.id}/delete`, { pin });
      await load();
      setDeleting(null);
      setPin("");
      toast.success(`Cabang ${b.name} dihapus permanen`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus cabang"); }
  };

  return (
    <div className="space-y-4" data-testid="branches-tab">
      {/* Tambah cabang */}
      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <h3 className="font-display font-black text-base mb-1">Tambah Cabang Baru</h3>
        <p className="text-[11px] text-stone-400 mb-3">Koordinat dipakai untuk deteksi GPS pelanggan & ongkir gratis dalam radius. Stok cabang baru mulai kosong (0). Email admin opsional — akun otomatis jadi admin setelah login Google pertama kali.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
          <input data-testid="branch-add-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama cabang * cth: FASMART Sanggau" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
          <input data-testid="branch-add-area" value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Alamat / area" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
          <input data-testid="branch-add-email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} placeholder="Email admin cabang (opsional)" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
          <input data-testid="branch-add-lat" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} type="number" step="any" placeholder="Latitude cth: -0.1581" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
          <input data-testid="branch-add-lng" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} type="number" step="any" placeholder="Longitude cth: 109.4161" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
          <input data-testid="branch-add-radius" value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: e.target.value })} type="number" step="any" placeholder="Radius ongkir gratis (KM)" className="border border-[#E5DECF] rounded-lg px-2 py-1.5" />
        </div>
        <button data-testid="branch-add-submit" onClick={createBranch} disabled={saving} className="mt-3 px-4 py-2 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> {saving ? "Menyimpan..." : "Tambah Cabang"}
        </button>
      </div>

      {/* Staff input produk */}
      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4" data-testid="staff-input-card">
        <h3 className="font-display font-black text-base mb-1">👷 Staff Input Produk</h3>
        <p className="text-[11px] text-stone-400 mb-3">Staff hanya bisa membuka tab Produk (input barcode, harga beli/modal, harga jual, stok) — tanpa akses keuangan/laporan/hapus data. Nonaktifkan kapan saja untuk mengembalikannya jadi konsumen biasa.</p>
        <div className="flex gap-1.5 items-center mb-3">
          <input data-testid="staff-add-email" value={staffEmail} onChange={(e) => setStaffEmail(e.target.value)} placeholder="Email staff (Google) cth: staff1@gmail.com" className="flex-1 text-xs border border-[#E5DECF] rounded-lg px-2 py-1.5 bg-white" />
          <button data-testid="staff-add-btn" onClick={addStaff} className="px-3 py-1.5 rounded-full bg-emerald-700 text-white text-[11px] font-bold flex items-center gap-1"><Plus className="w-3 h-3" /> Tambah Staff</button>
        </div>
        <div className="space-y-1.5">
          {staff.map((s) => (
            <div key={s.user_id} data-testid={`staff-row-${s.email}`} className="flex items-center gap-2 text-xs bg-stone-50 rounded-xl px-3 py-2">
              <span className="flex-1 min-w-0 truncate font-semibold">{s.email}</span>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">AKTIF</span>
              <button data-testid={`staff-deactivate-${s.email}`} onClick={() => deactivateStaff(s.email)} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">Nonaktifkan</button>
            </div>
          ))}
          {!staff.length && <p className="text-xs text-stone-400">Belum ada staff input.</p>}
        </div>
      </div>

      {/* Daftar cabang */}
      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <h3 className="font-display font-black text-base mb-3">Daftar Cabang ({branches.length})</h3>
        <div className="space-y-2.5">
          {branches.map((b) => (
            <div key={b.id} data-testid={`branch-card-${b.id}`} className={`rounded-xl border p-3 ${b.is_active ? "border-[#E5DECF] bg-stone-50/60" : "border-stone-200 bg-stone-100 opacity-70"}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div>
                  <span className="font-bold text-sm">{b.name}</span>
                  <span className="text-[11px] text-stone-500 ml-2">{b.area || "—"}</span>
                  <span data-testid={`branch-status-${b.id}`} className={`ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${b.is_active ? "bg-emerald-100 text-emerald-700" : "bg-stone-200 text-stone-500"}`}>{b.is_active ? "AKTIF" : "NONAKTIF"}</span>
                </div>
                <div className="flex gap-1.5">
                  <button data-testid={`branch-edit-${b.id}`} onClick={() => startEdit(b)} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 flex items-center gap-1">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button data-testid={`branch-toggle-${b.id}`} onClick={() => toggleActive(b)} className={`text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${b.is_active ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
                    <Power className="w-3 h-3" /> {b.is_active ? "Nonaktifkan" : "Aktifkan"}
                  </button>
                  <button data-testid={`branch-delete-${b.id}`} onClick={() => { setDeleting(deleting === b.id ? null : b.id); setPin(""); }} className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-rose-50 text-rose-600 flex items-center gap-1">
                    <Trash2 className="w-3 h-3" /> Hapus
                  </button>
                </div>
              </div>
              <div className="text-[10px] font-mono text-stone-400 mb-2">📍 {b.lat}, {b.lng} · radius {b.radius_km} KM</div>
              {editing === b.id && (
                <div data-testid={`branch-edit-form-${b.id}`} className="mb-2 rounded-xl border border-sky-200 bg-sky-50/60 p-3">
                  <div className="text-xs font-bold text-sky-800 mb-1.5">Edit nama, alamat & titik koordinat</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 text-xs">
                    <input data-testid={`branch-edit-name-${b.id}`} value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Nama toko *" className="border border-sky-200 rounded-lg px-2 py-1.5 bg-white" />
                    <input data-testid={`branch-edit-area-${b.id}`} value={editForm.area} onChange={(e) => setEditForm({ ...editForm, area: e.target.value })} placeholder="Alamat / area" className="border border-sky-200 rounded-lg px-2 py-1.5 bg-white" />
                    <input data-testid={`branch-edit-radius-${b.id}`} type="number" step="any" value={editForm.radius_km} onChange={(e) => setEditForm({ ...editForm, radius_km: e.target.value })} placeholder="Radius ongkir gratis (KM)" className="border border-sky-200 rounded-lg px-2 py-1.5 bg-white" />
                    <input data-testid={`branch-edit-lat-${b.id}`} type="number" step="any" value={editForm.lat} onChange={(e) => setEditForm({ ...editForm, lat: e.target.value })} placeholder="Latitude cth: -0.1581" className="border border-sky-200 rounded-lg px-2 py-1.5 bg-white" />
                    <input data-testid={`branch-edit-lng-${b.id}`} type="number" step="any" value={editForm.lng} onChange={(e) => setEditForm({ ...editForm, lng: e.target.value })} placeholder="Longitude cth: 109.4161" className="border border-sky-200 rounded-lg px-2 py-1.5 bg-white" />
                    <div className="flex gap-1.5">
                      <button data-testid={`branch-edit-save-${b.id}`} onClick={() => saveEdit(b)} className="flex-1 px-3 py-1.5 rounded-full bg-emerald-700 text-white text-[11px] font-bold">Simpan</button>
                      <button data-testid={`branch-edit-cancel-${b.id}`} onClick={() => setEditing(null)} className="px-3 py-1.5 rounded-full bg-white border border-[#E5DECF] text-stone-600 text-[11px] font-bold">Batal</button>
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-1.5" data-testid={`branch-emails-${b.id}`}>
                <div className="flex flex-wrap gap-1">
                  {branchEmails(b).map((em) => (
                    <span key={em} data-testid={`branch-email-chip-${b.id}-${em}`} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5">
                      {em}
                      <button data-testid={`branch-email-rm-${b.id}-${em}`} onClick={() => removeEmail(b, em)} title="Lepas email ini" className="text-rose-500 hover:text-rose-700 font-black">×</button>
                    </span>
                  ))}
                  {!branchEmails(b).length && <span className="text-[10px] text-stone-400">Belum ada email kasir/admin</span>}
                </div>
                {branchEmails(b).length < 5 && (
                  <div className="flex gap-1.5 items-center">
                    <input
                      data-testid={`branch-email-${b.id}`}
                      value={emailEdits[b.id] ?? ""}
                      onChange={(e) => setEmailEdits({ ...emailEdits, [b.id]: e.target.value })}
                      placeholder={`Tambah email kasir/admin (${branchEmails(b).length}/5) — ganti shift tinggal login email masing-masing`}
                      className="flex-1 text-xs border border-[#E5DECF] rounded-lg px-2 py-1.5 bg-white"
                    />
                    <button data-testid={`branch-email-save-${b.id}`} onClick={() => addEmail(b)} className="px-3 py-1.5 rounded-full bg-emerald-700 text-white text-[11px] font-bold flex items-center gap-1">
                      <Save className="w-3 h-3" /> Tambah
                    </button>
                  </div>
                )}
              </div>
              {deleting === b.id && (
                <div data-testid={`branch-delete-confirm-${b.id}`} className="mt-2 rounded-xl border border-rose-200 bg-rose-50 p-3">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-rose-700 mb-1.5"><ShieldAlert className="w-3.5 h-3.5" /> Hapus permanen {b.name}? Riwayat pesanan/keuangan tidak ikut terhapus, tapi cabang tidak bisa dikembalikan.</div>
                  <div className="flex gap-1.5 items-center">
                    <input data-testid={`branch-delete-pin-${b.id}`} type="password" inputMode="numeric" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="Masukkan PIN" className="w-40 text-xs border border-rose-300 rounded-lg px-2 py-1.5 bg-white" />
                    <button data-testid={`branch-delete-confirm-btn-${b.id}`} onClick={() => deleteBranch(b)} className="px-3 py-1.5 rounded-full bg-rose-600 text-white text-[11px] font-bold">Ya, Hapus</button>
                    <button data-testid={`branch-delete-cancel-${b.id}`} onClick={() => { setDeleting(null); setPin(""); }} className="px-3 py-1.5 rounded-full bg-white border border-[#E5DECF] text-stone-600 text-[11px] font-bold">Batal</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
